import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import QuoteManagement from "../crm/QuoteManagement";
import { AgreementsPage } from "../agreements/AgreementsPage";
import { UnderwriterQuoteForm } from "./UnderwriterQuoteForm";
import { DealQuoteSheet } from "./DealQuoteSheet";
import { Customer, Quote } from "@/types/crm";

export function QuotesPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast({
        title: "Error",
        description: "Failed to fetch customers",
        variant: "destructive",
      });
    }
  };

  const fetchQuotes = async () => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select(`
          *,
          customers (
            name,
            email,
            company
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast({
        title: "Error",
        description: "Failed to fetch quotes",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchCustomers(), fetchQuotes()])
      .finally(() => setIsLoading(false));
  }, []);

  const refreshData = () => {
    fetchCustomers();
    fetchQuotes();
  };

  if (isLoading) {
    return <div className="text-center">Loading quotes data...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Quotes & Agreements</h2>
          <p className="text-muted-foreground">Create and manage customer quotes and purchase agreements</p>
        </div>
        <Button onClick={refreshData} variant="outline">
          Refresh Data
        </Button>
      </div>

      <Tabs defaultValue="deal-quote" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="deal-quote">Deal Quote Sheet</TabsTrigger>
          <TabsTrigger value="quotes">Quotes Management</TabsTrigger>
          <TabsTrigger value="underwriter">Underwriter Requests</TabsTrigger>
          <TabsTrigger value="agreements">Purchase Agreements</TabsTrigger>
        </TabsList>
        
        <TabsContent value="deal-quote" className="space-y-4">
          <DealQuoteSheet />
        </TabsContent>
        
        <TabsContent value="quotes" className="space-y-4">
          <QuoteManagement 
            quotes={quotes}
            customers={customers}
            onRefresh={refreshData}
          />
        </TabsContent>
        
        <TabsContent value="underwriter" className="space-y-4">
          <UnderwriterQuoteForm />
        </TabsContent>
        
        <TabsContent value="agreements" className="space-y-4">
          <AgreementsPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}